import { useEffect, useMemo, useRef, useState } from "react"
import { useHub } from "../../core/Global"
import { onTechClock, onTechDeviceState, onTechLive, techClock, techDeviceState, watchTechClocks } from "../../core/TechLive"
import { HereGeocodePosition } from "../../models/MapModels"
import Technician from "../../modules/technicians/models/technician"
import { formatDuration, formatMiles, isRealPosition, milesBetween, resolveTechPosition, timeAgo } from "./mapUtils"

/**
 * Technicians for a job, the way every place that assigns one shows them: where each one is,
 * the drive to the job along the roads with current traffic, whether they are free right now,
 * and in what order to list them. Used by the Metro & Technician drawer and the add-job form, so
 * the two never rank or describe a technician differently.
 */

export interface TechRankInfo {
    hasApp: boolean
    platform?: string
    onClock: boolean
    clockAt?: Date
    live: boolean
    /** The drive in miles once known, else the straight line; undefined without positions. */
    miles?: number
    driveSeconds?: number
    /** Distance from an old position, kept only for want of anything better. */
    stale: boolean
    availableNow: boolean
    /** "2.3 mi · 6 min drive", or "~1.6 mi away" for a straight line. */
    distanceLabel?: string
    /** Where the distance is from: "live", "25 min ago", "from wake-up address", "last seen …". */
    distanceNote?: string
    /** For the route drawn on a map: "6 min · 2.3 mi", the same figures as the row. */
    routeLabel?: string
}

/**
 * How a list is ordered after "free right now", which always comes first: by distance (the
 * default), or by priority -- the job's metro and job type together, as the HUB orders them:
 * a priority in both first (the lower the two numbers add up, the higher), then a priority in
 * one (by its number), then the rest; by distance inside each.
 */
export type TechSortBy = "distance" | "priority"

const priorityTier = (t: Technician) => (t.metroPriority && t.typePriority) ? 0 : (t.metroPriority || t.typePriority) ? 1 : 2
const priorityScore = (t: Technician) => (t.metroPriority && t.typePriority)
    ? t.metroPriority + t.typePriority
    : t.metroPriority || t.typePriority || Infinity

export interface RankedTech {
    item: Technician
    info: TechRankInfo
}

// ~110 m: a technician driving around is not asked about again at every report.
const positionKey = (p: HereGeocodePosition) => `${p.lat.toFixed(3)},${p.lng.toFixed(3)}`

/** How long a drive that never answers may hold the list back. */
const LOAD_TIMEOUT_MS = 8000

export function useTechRanking(techs: Technician[] | undefined, jobPosition: HereGeocodePosition | undefined) {

    const jobKey = isRealPosition(jobPosition) ? positionKey(jobPosition) : ""
    const hub = useHub()

    // Who is on the clock, what their phone last said and where they are, kept live. Any change
    // re-renders; the figures are read from the stores directly.
    const [liveTick, liveTickSet] = useState<number>(0)
    useEffect(() => {
        watchTechClocks()
        const bump = () => liveTickSet(t => t + 1)
        const offClock = onTechClock(bump)
        const offDevice = onTechDeviceState(bump)
        const offLive = onTechLive(bump)
        // A live position ages out of "live" without any event, so look again now and then.
        const timer = window.setInterval(bump, 30000)
        return () => { offClock(); offDevice(); offLive(); window.clearInterval(timer) }
    }, [])

    // The drive from each technician to the job, from the HUB (HERE Matrix Routing). `key` is the
    // trip it was worked out for -- the technician's position and the job's -- so a technician who
    // moves, or a job whose address changes, is asked about again. No figures (too far, no road)
    // keeps the straight line.
    const [driving, drivingSet] = useState<Record<string, { key: string, meters?: number, seconds?: number }>>({})

    const drivingWanted = useMemo(() => {
        void liveTick
        if (!jobKey) return []
        return (techs ?? [])
            .map(t => ({ id: t.id, position: resolveTechPosition(t)?.position }))
            .filter((t): t is { id: string, position: HereGeocodePosition } => !!t.id && !!t.position)
            .filter(t => driving[t.id]?.key !== positionKey(t.position) + ">" + jobKey)
    }, [techs, driving, liveTick, jobKey])

    useEffect(() => {
        if (!drivingWanted.length || !isRealPosition(jobPosition)) return
        // A short wait, so a list arriving and a few live reports landing together are one request.
        const timer = window.setTimeout(() => {
            const asked = drivingWanted
            const askedFor = jobKey
            const from: Record<string, HereGeocodePosition> = {}
            asked.forEach(t => from[t.id] = t.position)
            hub.Post("/maps/routes/drivematrix", { to: jobPosition, from }, res => {
                const legs: Record<string, { lengthMeters: number, durationSeconds: number } | null> = res.success ? (res.data ?? {}) : {}
                drivingSet(d => {
                    const next = { ...d }
                    asked.forEach(t => {
                        const leg = legs[t.id]
                        next[t.id] = { key: positionKey(t.position) + ">" + askedFor, meters: leg?.lengthMeters, seconds: leg?.durationSeconds }
                    })
                    return next
                })
            })
        }, 300)
        return () => window.clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drivingWanted.map(t => t.id + "@" + positionKey(t.position)).join("|"), jobKey])

    // Ready to be shown in its final order: the technicians are in and so are their drives. A
    // caller that fetches a new list says so with beginLoad(); later moves of a live technician
    // are worked out without going back to "loading".
    const [ready, readySet] = useState<boolean>(false)
    const loadRef = useRef<{ id: number, techs?: Technician[] }>({ id: 0 })
    const techsRef = useRef(techs)
    techsRef.current = techs

    useEffect(() => {
        if (!ready && techs && techs !== loadRef.current.techs && drivingWanted.length === 0) readySet(true)
    }, [ready, techs, drivingWanted.length])

    const beginLoad = () => {
        const id = ++loadRef.current.id
        loadRef.current.techs = techsRef.current
        readySet(false)
        window.setTimeout(() => { if (id === loadRef.current.id) readySet(true) }, LOAD_TIMEOUT_MS)
    }
    const endLoad = () => readySet(true)

    const infoFor = (item: Technician): TechRankInfo => {
        void liveTick
        const device = techDeviceState(item.id)
        const hasApp = !!device && (!!device.platform || !!device.reportedAt || !!device.locationStatus)
        const clock = techClock(item.id)
        const resolved = resolveTechPosition(item)
        const live = resolved?.source === "live"

        const straight = milesBetween(resolved?.position, jobPosition)
        const drive = item.id && resolved ? driving[item.id] : undefined
        const driven = drive && drive.key === positionKey(resolved!.position) + ">" + jobKey && drive.meters != null ? drive : undefined
        const miles = driven ? driven.meters! / 1609.344 : straight
        const driveSeconds = driven?.seconds

        const age = resolved?.at ? Date.now() - resolved.at.getTime() : 0
        const distanceNote = miles == null ? undefined
            : resolved?.source === "live" ? "live"
            : resolved?.source === "recent" ? timeAgo(age)
            : resolved?.source === "wakeup" ? "from wake-up address"
            : "last seen " + timeAgo(age)

        // Free to take the job now: on the clock for an app technician, otherwise inside their
        // working hours (or always available).
        const a = item.availability
        const availableNow = hasApp ? clock?.on === true : (!!a?.alwaysAvailable || a?.openNow === true)

        return {
            hasApp, platform: device?.platform, onClock: clock?.on === true, clockAt: clock?.at, live,
            miles, driveSeconds, stale: resolved?.source === "last", availableNow, distanceNote,
            distanceLabel: miles == null ? undefined
                : driven ? `${formatMiles(miles)} · ${formatDuration(driveSeconds ?? 0)} drive`
                : `~${formatMiles(miles)} away`,
            routeLabel: driven ? `${formatDuration(driveSeconds ?? 0)} · ${formatMiles(miles!)}` : undefined
        }
    }

    // The order, so that it reads the way it looks: those free right now first, then the rest;
    // inside each, by the distance shown, nearest first, unknown last. Only when two are about as
    // near (within a mile) does the rest decide: a priority technician of the job's metro or job
    // type first, then the one with fewer active jobs.
    const compare = (a: RankedTech, b: RankedTech, sortBy: TechSortBy) => {
        if (a.info.availableNow !== b.info.availableNow) return a.info.availableNow ? -1 : 1
        const am = a.info.miles ?? Infinity, bm = b.info.miles ?? Infinity
        if (sortBy === "priority") {
            const byPriority = priorityTier(a.item) - priorityTier(b.item)
            if (byPriority) return byPriority
            const as = priorityScore(a.item), bs = priorityScore(b.item)
            if (as !== bs) return as < bs ? -1 : 1
            if (am !== bm) return am < bm ? -1 : 1
            return a.item.fullName.localeCompare(b.item.fullName)
        }
        if (Math.abs(am - bm) >= 1 || am === Infinity || bm === Infinity) {
            if (am !== bm) return am < bm ? -1 : 1
        }
        else {
            const prio = (t: Technician) => (t.metroPriority ? 1 : 0) + (t.typePriority ? 1 : 0)
            const byRest = prio(b.item) - prio(a.item) || (a.item.activeJobs ?? 0) - (b.item.activeJobs ?? 0) || am - bm
            if (byRest) return byRest
        }
        return a.item.fullName.localeCompare(b.item.fullName)
    }

    /** The technicians in order; `pinnedId` (the job's current technician) stays on top. */
    const rank = (list: Technician[] | undefined, pinnedId?: string, sortBy: TechSortBy = "distance"): RankedTech[] =>
        (list ?? [])
            .map(item => ({ item, info: infoFor(item) }))
            .sort((a, b) => {
                if (pinnedId && a.item.id === pinnedId) return -1
                if (pinnedId && b.item.id === pinnedId) return 1
                return compare(a, b, sortBy)
            })

    return { infoFor, rank, ready, beginLoad, endLoad, liveTick }
}
